import React, { useState, useEffect } from 'react';
import chatApi from '../../services/chatApi';
import { useToast } from '../ToastProvider';
import './ChannelManagementModal.css';

const MAX_PHOTO_SIZE_BYTES = 500 * 1024 * 1024;

const ChannelManagementModal = ({ isOpen, onClose, channel, onUpdate, onDelete, onUnsubscribe }) => {
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'members', 'admins'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    avatar: null
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen && channel) {
      loadChannelDetails();
    }
  }, [isOpen, channel]);

  const loadChannelDetails = async () => {
    if (!channel) return;
    
    setLoadingData(true);
    try {
      const details = await chatApi.getChannelDetails(channel.id);
      setIsAdmin(details.isAdmin);
      
      // Проверяем, является ли текущий пользователь создателем
      const currentUser = await chatApi.getMe();
      setIsCreator(details.createdBy === currentUser.id || details.createdBy === currentUser.userId);
      
      setFormData({
        name: details.name || '',
        description: details.description || '',
        avatar: details.avatar || null
      });
      setAvatarPreview(details.avatar || null);
      
      // Загружаем подписчиков и админов из деталей
      if (details.admins) {
        setAdmins(details.admins);
      }
      
      // Загружаем всех подписчиков
      if (details.subscribers) {
        setSubscribers(details.subscribers);
      } else {
        // Fallback на админов, если subscribers не пришли
        setSubscribers(details.admins || []);
      }
    } catch (err) {
      console.error('Failed to load channel details:', err);
      setError('Не удалось загрузить информацию о канале');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        setError('Размер файла не должен превышать 500 МБ');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setAvatarPreview(base64);
        setFormData({ ...formData, avatar: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateChannel = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      setError('Только администраторы могут редактировать канал');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const updated = await chatApi.updateChannel(channel.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        avatar: formData.avatar || null
      });

      if (onUpdate) {
        onUpdate(updated);
      }
      
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при обновлении канала');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChannel = async () => {
    setLoading(true);
    try {
      await chatApi.deleteChannel(channel.id);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при удалении канала');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      await chatApi.unsubscribeFromChannel(channel.id);
      if (onUnsubscribe) {
        onUnsubscribe();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Ошибка при отписке от канала');
    } finally {
      setLoading(false);
      setShowUnsubscribeConfirm(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/chat/channel/${channel.id}`;
    navigator.clipboard.writeText(link).then(() => {
      showToast('Ссылка скопирована в буфер обмена!', { type: 'success' });
    }).catch(() => {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('Ссылка скопирована в буфер обмена!', { type: 'success' });
    });
  };

  const handleClose = () => {
    setActiveTab('info');
    setShowDeleteConfirm(false);
    setShowUnsubscribeConfirm(false);
    setError(null);
    onClose();
  };

  if (!isOpen || !channel) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Управление каналом</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-tabs">
          <button
            className={activeTab === 'info' ? 'active' : ''}
            onClick={() => setActiveTab('info')}
          >
            Информация
          </button>
          <button
            className={activeTab === 'members' ? 'active' : ''}
            onClick={() => setActiveTab('members')}
          >
            Подписчики
          </button>
          {isAdmin && (
            <button
              className={activeTab === 'admins' ? 'active' : ''}
              onClick={() => setActiveTab('admins')}
            >
              Администраторы
            </button>
          )}
        </div>

        {loadingData ? (
          <div className="loading-state">Загрузка...</div>
        ) : (
          <>
            {activeTab === 'info' && (
              <form onSubmit={handleUpdateChannel} className="modal-form">
                {error && <div className="error-message">{error}</div>}
                
                {!isAdmin && (
                  <div className="info-message">
                    Только администраторы могут редактировать информацию о канале
                  </div>
                )}
                
                <div className="form-group">
                  <label>Аватар канала</label>
                  <div className="avatar-upload">
                    <div className="avatar-preview">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Preview" />
                      ) : (
                        <div className="avatar-placeholder">📢</div>
                      )}
                    </div>
                    {isAdmin && (
                      <>
                        <label className="avatar-upload-button">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarChange}
                            style={{ display: 'none' }}
                          />
                          {avatarPreview ? 'Изменить' : 'Загрузить аватар'}
                        </label>
                        {avatarPreview && (
                          <button
                            type="button"
                            className="remove-avatar-button"
                            onClick={() => {
                              setAvatarPreview(null);
                              setFormData({ ...formData, avatar: null });
                            }}
                          >
                            Удалить
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Название канала *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название канала"
                    required
                    maxLength={100}
                    disabled={!isAdmin}
                  />
                </div>

                <div className="form-group">
                  <label>Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Введите описание канала"
                    rows={4}
                    maxLength={500}
                    disabled={!isAdmin}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="btn-copy-link"
                    title="Скопировать ссылку на канал"
                  >
                    📋 Копировать ссылку
                  </button>
                </div>

                {isAdmin && (
                  <div className="form-actions">
                    <button type="button" onClick={handleClose} className="btn-cancel">
                      Отмена
                    </button>
                    <button type="submit" disabled={loading} className="btn-submit">
                      {loading ? 'Сохранение...' : 'Сохранить изменения'}
                    </button>
                  </div>
                )}

                <div className="danger-zone">
                  <h3>Опасная зона</h3>
                  {isCreator ? (
                    <>
                      <p>Вы являетесь создателем этого канала. Вы можете удалить его.</p>
                      {!showDeleteConfirm ? (
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="btn-danger"
                        >
                          Удалить канал
                        </button>
                      ) : (
                        <div className="confirm-delete">
                          <p>Вы уверены, что хотите удалить канал? Это действие нельзя отменить.</p>
                          <div className="confirm-actions">
                            <button
                              type="button"
                              onClick={() => setShowDeleteConfirm(false)}
                              className="btn-cancel"
                            >
                              Отмена
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteChannel}
                              disabled={loading}
                              className="btn-danger"
                            >
                              {loading ? 'Удаление...' : 'Да, удалить'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p>Вы можете отписаться от этого канала.</p>
                      {!showUnsubscribeConfirm ? (
                        <button
                          type="button"
                          onClick={() => setShowUnsubscribeConfirm(true)}
                          className="btn-danger-outline"
                        >
                          Отписаться от канала
                        </button>
                      ) : (
                        <div className="confirm-delete">
                          <p>Вы уверены, что хотите отписаться от канала?</p>
                          <div className="confirm-actions">
                            <button
                              type="button"
                              onClick={() => setShowUnsubscribeConfirm(false)}
                              className="btn-cancel"
                            >
                              Отмена
                            </button>
                            <button
                              type="button"
                              onClick={handleUnsubscribe}
                              disabled={loading}
                              className="btn-danger-outline"
                            >
                              {loading ? 'Отписка...' : 'Да, отписаться'}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </form>
            )}

            {activeTab === 'members' && (
              <div className="modal-form">
                <div className="form-group">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск подписчиков..."
                    className="member-search"
                  />
                </div>
                <div className="members-list">
                  {subscribers.length === 0 ? (
                    <div className="empty-members">Нет подписчиков</div>
                  ) : (
                    subscribers
                      .filter(sub => {
                        if (!searchQuery.trim()) return true;
                        const query = searchQuery.toLowerCase();
                        const name = ((sub.displayName || sub.username || '') || '').toLowerCase();
                        return name.includes(query);
                      })
                      .map(sub => (
                        <div key={sub.id} className="member-item">
                          <div className="member-avatar">
                            {sub.avatar ? (
                              <img src={sub.avatar} alt="Avatar" />
                            ) : (
                              <span>{((sub.displayName || sub.username || '?')[0] || '?').toUpperCase()}</span>
                            )}
                          </div>
                          <div className="member-info">
                            <div className="member-name">
                              {sub.displayName || sub.username}
                            </div>
                            {sub.username && (
                              <div className="member-username">@{sub.username}</div>
                            )}
                          </div>
                          {sub.isAdmin && (
                            <div className="admin-badge">Админ</div>
                          )}
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'admins' && isAdmin && (
              <div className="modal-form">
                <div className="info-message">
                  Список администраторов канала
                </div>
                <div className="members-list">
                  {admins.length === 0 ? (
                    <div className="empty-members">Нет администраторов</div>
                  ) : (
                    admins.map(admin => (
                      <div key={admin.id} className="member-item">
                        <div className="member-avatar">
                          {admin.avatar ? (
                            <img src={admin.avatar} alt="Avatar" />
                          ) : (
                            <span>{((admin.displayName || admin.username || '?')[0] || '?').toUpperCase()}</span>
                          )}
                        </div>
                        <div className="member-info">
                          <div className="member-name">
                            {admin.displayName || admin.username}
                          </div>
                          {admin.username && (
                            <div className="member-username">@{admin.username}</div>
                          )}
                        </div>
                        <div className="admin-badge">Админ</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChannelManagementModal;
